"use client"

import React, { useEffect, useRef, useState } from "react"
import { addAddressAction, updateAddressAction } from "@/actions/userActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { SubmitHandler, useForm } from "react-hook-form"
import { z } from "zod"

import { countries } from "@/lib/helper"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"

// settings for development purposes
const DEV_INSERT_DEFAULTS = false

interface EditOrCreateAddressProps {
  address?: ExtendedAddressRO
  customer?: UserData
  user?: UserData
  onSave?: (address: ExtendedAddressRO, user: UserData) => void
  mode: "add" | "edit" | "initial"
  type: "invoiceTo" | "shipTo" | "general"
}

export default function EditOrCreateAddress({ address, customer, user, onSave, mode, type }: EditOrCreateAddressProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (address) {
      form.reset(address)
    } else {
      form.reset({})
    }
  }, [address])

  const t = useTranslations("storefront")
  const { isB2BCustomer } = useUbStorefrontState()

  const validationSchema = z
    .object({
      id: z.string().optional(),
      title: z.enum([t("salutation.ms"), t("salutation.mr"), t("salutation.divers")], {
        required_error: t("forms.edit_address_form.error.title"),
      }),
      honorific: z.string().max(64, t("forms.edit_address_form.error.honorific_max")).optional(),
      firstName: z
        .string({ required_error: t("forms.edit_address_form.error.first_name") })
        .min(2, t("forms.edit_address_form.error.first_name"))
        .max(64, t("forms.edit_address_form.error.first_name_max")),
      lastName: z
        .string({ required_error: t("forms.edit_address_form.error.last_name") })
        .min(2, t("forms.edit_address_form.error.last_name"))
        .max(64, t("forms.edit_address_form.error.last_name_max")),
      companyName1: isB2BCustomer
        ? z
            .string({
              required_error: t("forms.edit_address_form.error.company"),
            })
            .min(2, t("forms.edit_address_form.error.company"))
            .max(64, t("forms.edit_address_form.error.company_max"))
        : z.string().optional(),
      companyName2: z.string().max(64, t("forms.edit_address_form.error.company_max")).optional(),
      taxationID: z.string().max(64, t("forms.edit_address_form.error.taxation_id_max")).optional(),
      addressLine1: z
        .string({ required_error: t("forms.edit_address_form.error.address_line") })
        .min(2, t("forms.edit_address_form.error.address_line"))
        .max(128, t("forms.edit_address_form.error.address_line_max")),
      addressLine2: z.string().max(128, t("forms.edit_address_form.error.address_line_max")).optional(),
      postalCode: z
        .string({ required_error: t("forms.edit_address_form.error.postal_code") })
        .regex(/\d{4,5}$/, t("forms.edit_address_form.error.postal_code")),
      city: z
        .string({
          required_error: t("forms.edit_address_form.error.city"),
        })
        .min(2, t("forms.edit_address_form.error.city"))
        .max(128, t("forms.edit_address_form.error.city_max")),
      country: z.enum(
        [
          t("country.germany"),
          t("country.austria"),
          t("country.switzerland"),
          t("country.france"),
          t("country.netherlands"),
        ],
        {
          required_error: t("forms.edit_address_form.error.country"),
        }
      ),
      countryCode: z.string().optional(),
      email: z
        .string(/*{
        required_error: t("forms.edit_address_form.error.email"),
      }*/)
        .email(t("forms.edit_address_form.error.email")),
      phoneBusiness: z
        .string()
        .min(2, t("forms.edit_address_form.error.phone"))
        .max(32, t("forms.edit_address_form.error.phone_max"))
        .optional(),
      invoiceToAddress: z.boolean(),
      isPreferredInvoiceToAddress: z.boolean().optional(),
      shipToAddress: z.boolean(),
      isPreferredShipToAddress: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      // Regel 1: Mindestens eine der Haupt-Adressarten muss ausgewählt sein.
      // Diese Logik bleibt unverändert, da sie auf nicht-optionalen Feldern operiert.
      if (!data.invoiceToAddress && !data.shipToAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Mindestens eine Adressart (Rechnung oder Lieferadresse) muss ausgewählt werden.",
          path: ["invoiceToAddress"],
        })
      }

      // Regel 2: "Bevorzugte Rechnungsadresse" kann nur `true` sein,
      // wenn "Rechnungsadresse" ebenfalls `true` ist.
      // Wir prüfen explizit auf `=== true`, da das Feld auch `undefined` sein kann.
      if (data.isPreferredInvoiceToAddress === true && !data.invoiceToAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Kann nur die bevorzugte Rechnungsadresse sein, wenn sie auch als Rechnungsadresse markiert ist.",
          path: ["isPreferredInvoiceToAddress"],
        })
      }

      // Regel 3: "Bevorzugte Versandadresse" kann nur `true` sein,
      // wenn "Versandadresse" ebenfalls `true` ist.
      if (data.isPreferredShipToAddress === true && !data.shipToAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Kann nur die bevorzugte Lieferadresse sein, wenn sie auch als Lieferadresse markiert ist.",
          path: ["isPreferredShipToAddress"],
        })
      }
    })

  type ValidationSchemaType = z.infer<typeof validationSchema>

  const form = useForm<ValidationSchemaType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      id: address?.id ?? "",
      title: DEV_INSERT_DEFAULTS ? "Herr" : (address?.title ?? ""),
      honorific: DEV_INSERT_DEFAULTS ? "jjj" : (address?.honorific ?? ""),
      firstName: DEV_INSERT_DEFAULTS ? "Jon" : (address?.firstName ?? ""),
      lastName: DEV_INSERT_DEFAULTS ? "Doe" : (address?.lastName ?? ""),
      companyName1: DEV_INSERT_DEFAULTS ? "Acme Company" : (address?.companyName1 ?? ""),
      companyName2: DEV_INSERT_DEFAULTS ? "Empfang" : (address?.companyName2 ?? ""),
      taxationID: DEV_INSERT_DEFAULTS ? "Tax" : (customer?.["taxationID"] ?? ""),
      addressLine1: DEV_INSERT_DEFAULTS ? "Dummystr. 4" : (address?.addressLine1 ?? ""),
      addressLine2: DEV_INSERT_DEFAULTS ? "1st Floor" : (address?.addressLine2 ?? ""),
      postalCode: DEV_INSERT_DEFAULTS ? "08123" : (address?.postalCode ?? ""),
      city: DEV_INSERT_DEFAULTS ? "Berlin" : (address?.city ?? ""),
      country: DEV_INSERT_DEFAULTS ? "Deutschland" : (address?.country ?? ""),
      countryCode: DEV_INSERT_DEFAULTS ? "DE" : (address?.countryCode ?? ""),
      email: DEV_INSERT_DEFAULTS ? "acme@acme.com" : (address?.email ?? ""),
      phoneBusiness: DEV_INSERT_DEFAULTS ? "+49321123123" : (address?.phoneBusiness ?? ""),
      invoiceToAddress: DEV_INSERT_DEFAULTS ? false : address?.invoiceToAddress === true,
      isPreferredInvoiceToAddress:
        (address && user?.preferredInvoiceToAddress?.id === address.id) || mode === "initial",
      shipToAddress: DEV_INSERT_DEFAULTS ? false : address?.shipToAddress === true,
      isPreferredShipToAddress: (address && user?.preferredShipToAddress?.id === address.id) || mode === "initial",
    },
  })

  const onSubmit: SubmitHandler<ValidationSchemaType> = async (data: ValidationSchemaType, event) => {
    console.log("onSubmit", data)
    event && event.preventDefault()
    setSaving(true)

    // Set countryCode based on country selection
    const countryMap: Record<string, string> = {
      [t("country.germany")]: "DE",
      [t("country.austria")]: "AT",
      [t("country.switzerland")]: "CH",
      [t("country.france")]: "FR",
      [t("country.netherlands")]: "NL",
    }
    data.countryCode = countryMap[data.country] || ""

    let newAddress: ExtendedAddressRO = {
      ...data,
      shipFromAddress: false,
      installToAddress: false,
      serviceToAddress: false,
    }

    if (type === "invoiceTo") {
      newAddress.invoiceToAddress = true
    }

    if (type === "shipTo") {
      newAddress.shipToAddress = true
    }

    let newUser = {
      ...user,
      preferredInvoiceToAddress: data.isPreferredInvoiceToAddress ? newAddress : user?.preferredInvoiceToAddress,
      preferredShipToAddress: data.isPreferredShipToAddress ? newAddress : user?.preferredShipToAddress,
    } as UserData

    if (mode === "edit") {
      if (customer?.customerNo && address?.id) {
        console.log("Update address: ")
        console.dir(newAddress)

        const resultAddress = await updateAddressAction(customer?.customerNo, address?.id, newAddress)

        console.log("Updated address: ")
        console.dir(resultAddress)

        if (onSave) {
          onSave(newAddress, newUser)
        }
      }
    } else if (mode === "add" || mode === "initial") {
      if (customer?.customerNo) {
        // The address link looks like this:
        // UDOBAER-UBDE-Site/rest;loc=de_DE;cur=EUR/customers/00028001/addresses/c.xkQBRQ5z4AAAGWVA8HztlU
        // We need to extract the last part after the last slash
        const createdAddressLink = await addAddressAction(customer.customerNo, newAddress)
        if (createdAddressLink?.uri) {
          const addressId = createdAddressLink.uri.split("/").pop()
          if (addressId) {
            newAddress.id = addressId
          }
        }
        console.log("newAddress", newAddress)
        onSave && onSave(newAddress, newUser)
      }
    }
    setSaving(false)
  }

  const styleWithoutOutline =
    "focus:ring-offset-0 focus-visible:ring-offset-0 focus:ring-0 focus:outline-0 focus-visible:outline-0 focus-visible:ring-0"
  const focusBorders = "focus:border-primary focus:border-2 focus-visible:border-primary focus-visible:border-2"
  const textFieldStyleBase = styleWithoutOutline + " " + focusBorders + " pl-4"
  const textFieldStyle = textFieldStyleBase + " md:pt-1.5 rounded-lg text-sm md:text-base h-9 md:h-10"
  const radioButtonStyle = styleWithoutOutline + " " + focusBorders + " text-sm md:text-base size-4 md:size-5"
  const selectStyle = styleWithoutOutline + " " + focusBorders + " text-base pt-1.5 rounded-lg"
  const checkboxStyle = styleWithoutOutline + " " + focusBorders + " mr-3  text-sm md:text-base size-4 md:size-5"

  return (
    <div
      className={cn("w-full", {
        "pointer-events-none cursor-wait": saving,
      })}
    >
      <Form {...form}>
        <form
          className="flex flex-col text-sm md:text-base"
          ref={formRef}
          name="EditAddressForm"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset className="flex flex-col gap-y-2 md:gap-y-3">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <RadioGroup className="flex flex-row" value={field.value} onValueChange={field.onChange}>
                      <div
                        className={cn("flex flex-row items-center justify-center gap-2", {
                          "ring-2 ring-offset-1 ring-red-500 rounded-sm ": !!form.formState.errors.title,
                        })}
                      >
                        <div className="flex flex-row items-center justify-center gap-2">
                          <RadioGroupItem className={radioButtonStyle} id="salutationMs" value={t("salutation.ms")} />
                          <label htmlFor="salutationMs">{t("salutation.ms")}</label>
                        </div>
                        <div className="flex flex-row items-center justify-center gap-2">
                          <RadioGroupItem className={radioButtonStyle} id="salutationMr" value={t("salutation.mr")} />
                          <label htmlFor="salutationMr">{t("salutation.mr")}</label>
                        </div>
                        <div className="flex flex-row items-center justify-center gap-2">
                          <RadioGroupItem
                            className={radioButtonStyle}
                            id="salutationDivers"
                            value={t("salutation.divers")}
                          />
                          <label htmlFor="salutationDivers">{t("salutation.divers")}</label>
                        </div>
                      </div>
                      <span className="text-primary text-sm">*</span>
                    </RadioGroup>
                  </FormControl>
                  {/*  <FormMessage />*/}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="honorific"
              render={({ field }) => (
                <FormItem className="w-40">
                  <FormControl>
                    <Input
                      className={cn(textFieldStyle, {
                        "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.honorific,
                      })}
                      {...field}
                      placeholder={t("forms.edit_address_form.placeholder.honorific")}
                    />
                  </FormControl>
                  {/* <FormMessage />*/}
                </FormItem>
              )}
            />
            <div className="flex flex-row gap-x-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className={cn(textFieldStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.firstName,
                        })}
                        {...field}
                        placeholder={t("forms.edit_address_form.placeholder.first_name")}
                      />
                    </FormControl>
                    {/* <FormMessage />*/}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className={cn(textFieldStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.lastName,
                        })}
                        {...field}
                        placeholder={t("forms.edit_address_form.placeholder.last_name")}
                      />
                    </FormControl>
                    {/* <FormMessage />*/}
                  </FormItem>
                )}
              />
            </div>
            {isB2BCustomer && (
              <FormField
                control={form.control}
                name="companyName1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className={cn(textFieldStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.companyName1,
                        })}
                        {...field}
                        placeholder={t("forms.edit_address_form.placeholder.company1") + (isB2BCustomer && "*")}
                      />
                    </FormControl>
                    {/*  <FormMessage />*/}
                  </FormItem>
                )}
              />
            )}
            {isB2BCustomer && (
              <div className="flex flex-row gap-x-2">
                <FormField
                  control={form.control}
                  name="companyName2"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className={cn(textFieldStyle, {
                            "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.companyName2,
                          })}
                          {...field}
                          placeholder={t("forms.edit_address_form.placeholder.company2")}
                        />
                      </FormControl>
                      {/*  <FormMessage />*/}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="taxationID"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className={cn(textFieldStyle, {
                            "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.taxationID,
                          })}
                          {...field}
                          placeholder={t("forms.edit_address_form.placeholder.taxation_id")}
                        />
                      </FormControl>
                      {/*   <FormMessage />*/}
                    </FormItem>
                  )}
                />
              </div>
            )}
            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn(textFieldStyle, {
                        "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.addressLine1,
                      })}
                      {...field}
                      placeholder={t("forms.edit_address_form.placeholder.address_line_1")}
                    />
                  </FormControl>
                  {/* <FormMessage />*/}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn(textFieldStyle, {
                        "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.addressLine2,
                      })}
                      {...field}
                      placeholder={t("forms.edit_address_form.placeholder.address_line_2")}
                    />
                  </FormControl>
                  {/*   <FormMessage />*/}
                </FormItem>
              )}
            />
            <div className="flex flex-row gap-x-2">
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem className="w-[6.8rem]">
                    <FormControl>
                      <Input
                        className={cn(textFieldStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.postalCode,
                        })}
                        {...field}
                        placeholder={t("forms.edit_address_form.placeholder.postal_code")}
                      />
                    </FormControl>
                    {/* <FormMessage />*/}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        className={cn(textFieldStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.city,
                        })}
                        {...field}
                        placeholder={t("forms.edit_address_form.placeholder.city")}
                      />
                    </FormControl>
                    {/* <FormMessage />*/}
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className={cn(selectStyle, {
                          "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.country,
                        })}
                      >
                        <SelectValue
                          placeholder={
                            <span className="text-base text-muted-foreground">
                              {t("forms.edit_address_form.placeholder.country")}
                            </span>
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="z-above-all rounded-sm">
                        {countries.map((country) => (
                          <SelectItem className="cursor-pointer" value={t(`country.${country}`)} key={country}>
                            {t(`country.${country}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {/*           <FormMessage />*/}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn(textFieldStyle, {
                        "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.email,
                      })}
                      {...field}
                      placeholder={t("forms.edit_address_form.placeholder.email")}
                    />
                  </FormControl>
                  {/*  <FormMessage />*/}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phoneBusiness"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn(textFieldStyle, {
                        "ring-2 ring-offset-1 ring-red-500": !!form.formState.errors.phoneBusiness,
                      })}
                      {...field}
                      placeholder={t("forms.edit_address_form.placeholder.phone_business")}
                    />
                  </FormControl>
                  {/*  <FormMessage />*/}
                </FormItem>
              )}
            />
            <div className={"grid grid-cols-12 grid-rows-2 items-start justify-start gap-2"}>
              <FormField
                control={form.control}
                name="invoiceToAddress"
                render={({ field }) => (
                  <FormItem className={"col-span-5"}>
                    <FormControl>
                      <div className="flex flex-row items-center">
                        <Checkbox
                          disabled={type === "invoiceTo"}
                          id="invoiceToAddress"
                          className={checkboxStyle}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-xs" htmlFor="invoiceToAddress">
                          {t("forms.edit_address_form.placeholder.invoice_to_address")}
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPreferredInvoiceToAddress"
                render={({ field }) => (
                  <FormItem className={"col-span-7"}>
                    <FormControl>
                      <div className="flex flex-row items-center">
                        <Checkbox
                          id="isPreferredInvoiceToAddress"
                          disabled={mode === "initial"}
                          className={checkboxStyle}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-xs" htmlFor="isPreferredInvoiceToAddress">
                          {t("forms.edit_address_form.placeholder.preferred_invoice_to_address")}
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="shipToAddress"
                render={({ field }) => (
                  <FormItem className={"col-span-5"}>
                    <FormControl>
                      <div className="flex flex-row items-center">
                        <Checkbox
                          id="shipToAddress"
                          disabled={type === "shipTo" || mode === "initial"}
                          className={checkboxStyle}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-xs" htmlFor="shipToAddress">
                          {t("forms.edit_address_form.placeholder.ship_to_address")}
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPreferredShipToAddress"
                render={({ field }) => (
                  <FormItem className={"col-span-7"}>
                    <FormControl>
                      <div className="flex flex-row items-center">
                        <Checkbox
                          id="isPreferredShipToAddress"
                          disabled={mode === "initial"}
                          className={checkboxStyle}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-xs" htmlFor="isPreferredShipToAddress">
                          {t("forms.edit_address_form.placeholder.preferred_ship_to_address")}
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </fieldset>
          <Button type="submit" size="lg" className="mt-4 self-center md:mb-6 md:mt-10" disabled={saving}>
            {mode === "add" || mode === "initial"
              ? t("forms.edit_address_form.add")
              : t("forms.edit_address_form.save")}
          </Button>
        </form>
      </Form>
    </div>
  )
}
